import React, { useEffect, useMemo, useState } from "react";
import { fetchMatkaDailyResults } from "../../../api/services/matka";
import MatkaDateSelect from "./MatkaDateSelect";

export function todayIst() {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata" }).format(new Date());
}

export function formatDisplayDate(iso) {
  if (!iso) return "";
  const [year, month, day] = String(iso).split("-");
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function formatResultLine(row) {
  const open = row?.open_pana || "***";
  const close = row?.close_pana || "***";
  const jodi = row?.jodi_digit || "**";
  const pending = !row?.open_pana && !row?.close_pana && !row?.jodi_digit;
  return { open, jodi, close, text: `${open}-${jodi}-${close}`, pending };
}

function readError(error) {
  return error?.response?.data?.message || "Unable to load results.";
}

export default function MatkaDailyResults({ searchTerm = "" }) {
  const [date, setDate] = useState(todayIst);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!date) return undefined;

    let active = true;
    setLoading(true);
    setError("");

    fetchMatkaDailyResults(date)
      .then((data) => {
        if (active) setPayload(data);
      })
      .catch((err) => {
        if (!active) return;
        setPayload(null);
        setError(readError(err));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [date]);

  const results = useMemo(() => {
    const list = Array.isArray(payload?.results) ? payload.results : [];
    const query = searchTerm.trim().toLowerCase();
    if (!query) return list;
    return list.filter((row) =>
      String(row.market_name || "").toLowerCase().includes(query)
    );
  }, [payload, searchTerm]);

  const headingDate = formatDisplayDate(payload?.date || date);

  return (
    <div className="matka-results-panel">
      <div className="matka-results-toolbar">
        <div className="matka-results-field">
          <label className="matka-panna-bulk-label" htmlFor="matka-daily-date">
            Date
          </label>
          <MatkaDateSelect id="matka-daily-date" value={date} onChange={setDate} />
        </div>
      </div>

      {loading ? (
        <div className="matka-empty-state text-center py-5">
          <p className="text-light fs-5">Loading results...</p>
        </div>
      ) : error ? (
        <div className="matka-empty-state text-center py-5">
          <p className="matka-sangam-error fs-5">{error}</p>
        </div>
      ) : (
        <div className="matka-bids-list-card">
          <h3 className="matka-bids-list-title">Results for {headingDate}</h3>
          {results.length ? (
            <div className="matka-results-table-wrap">
              <table className="matka-bids-table">
                <thead>
                  <tr>
                    <th>Market</th>
                    <th>Result</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((row) => {
                    const result = formatResultLine(row);
                    const declared = row.status === "declared" || row.declared;
                    return (
                      <tr key={row.market_id}>
                        <td className="td-panna">{row.market_name}</td>
                        <td>
                          <span className={`matka-result-line${result.pending ? " is-pending" : ""}`}>
                            {result.text}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`matka-result-status ${
                              declared ? "is-declared" : "is-pending"
                            }`}
                          >
                            {declared ? "Declared" : "Pending"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="matka-empty-state text-center py-5">
              <p className="text-light fs-5">
                {searchTerm.trim()
                  ? `No results found matching "${searchTerm.trim()}"`
                  : "No results for this date."}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
